import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faUsers, faRandom, faArrowLeft, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface Participant {
  id: string;
  name: string;
}

interface Match {
  id: string;
  roundIndex: number;
  matchIndex: number;
  participant1: Participant | null;
  participant2: Participant | null;
  winner: Participant | null;
  isByeMatch: boolean;
}

type TournamentStep = 'setup' | 'bracket' | 'complete';

// ============================================================================
// TOURNAMENT COMPONENT
// ============================================================================

interface TournamentProps {
  onBack: () => void;
}

const Tournament: React.FC<TournamentProps> = ({ onBack }) => {
  // State
  const [step, setStep] = useState<TournamentStep>('setup');
  const [tournamentName, setTournamentName] = useState<string>('');
  const [participantInput, setParticipantInput] = useState<string>('');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [bracket, setBracket] = useState<Match[][]>([]);
  const [champion, setChampion] = useState<Participant | null>(null);

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Generate unique ID for participants and matches
   */
  const generateId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  /**
   * Shuffle array using Fisher-Yates algorithm
   */
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  /**
   * Calculate next power of 2 (for bracket slots)
   */
  const nextPowerOfTwo = (n: number): number => {
    return Math.pow(2, Math.ceil(Math.log2(n)));
  };

  /**
   * Get round name based on position
   */
  const getRoundName = (roundIndex: number, totalRounds: number): string => {
    const position = totalRounds - roundIndex;
    if (position === 1) return 'Final';
    if (position === 2) return 'Semi-Finals';
    if (position === 3) return 'Quarter-Finals';
    return `Round ${roundIndex + 1}`;
  };

  // ============================================================================
  // PARTICIPANT MANAGEMENT
  // ============================================================================

  const addParticipant = (): void => {
    const trimmed = participantInput.trim();
    if (!trimmed) return;

    // Check for duplicates
    const isDuplicate = participants.some(
      (p) => p.name.toLowerCase() === trimmed.toLowerCase()
    );

    if (isDuplicate) {
      alert('This participant already exists!');
      return;
    }

    const newParticipant: Participant = {
      id: generateId(),
      name: trimmed,
    };

    setParticipants([...participants, newParticipant]);
    setParticipantInput('');
  };

  const removeParticipant = (id: string): void => {
    setParticipants(participants.filter((p) => p.id !== id));
  };

  const shuffleParticipants = (): void => {
    setParticipants(shuffleArray(participants));
  };

  // ============================================================================
  // BRACKET GENERATION
  // ============================================================================

  const generateBracket = (): void => {
    if (participants.length < 2) {
      alert('Need at least 2 participants!');
      return;
    }

    if (!tournamentName.trim()) {
      alert('Please enter a tournament name!');
      return;
    }

    // Shuffle participants for fairness
    const shuffledParticipants = shuffleArray(participants);

    // Calculate bracket size and number of rounds
    const bracketSize = nextPowerOfTwo(shuffledParticipants.length);
    const totalRounds = Math.log2(bracketSize);
    const byeCount = bracketSize - shuffledParticipants.length;

    // Create all participants including byes
    const allSlots: (Participant | null)[] = [
      ...shuffledParticipants,
      ...Array(byeCount).fill(null),
    ];

    // Initialize bracket structure
    const newBracket: Match[][] = [];

    // Generate Round 1 (first round with all participants)
    const round1Matches: Match[] = [];
    for (let i = 0; i < allSlots.length; i += 2) {
      const participant1 = allSlots[i];
      const participant2 = allSlots[i + 1];
      const isByeMatch = participant1 === null || participant2 === null;

      const match: Match = {
        id: generateId(),
        roundIndex: 0,
        matchIndex: round1Matches.length,
        participant1,
        participant2,
        winner: isByeMatch ? (participant1 || participant2) : null,
        isByeMatch,
      };

      round1Matches.push(match);
    }

    newBracket.push(round1Matches);

    // Generate subsequent rounds (empty matches to be filled)
    for (let roundIdx = 1; roundIdx < totalRounds; roundIdx++) {
      const prevRoundMatches = newBracket[roundIdx - 1].length;
      const currentRoundMatches = prevRoundMatches / 2;
      const roundMatches: Match[] = [];

      for (let matchIdx = 0; matchIdx < currentRoundMatches; matchIdx++) {
        const match: Match = {
          id: generateId(),
          roundIndex: roundIdx,
          matchIndex: matchIdx,
          participant1: null,
          participant2: null,
          winner: null,
          isByeMatch: false,
        };

        roundMatches.push(match);
      }

      newBracket.push(roundMatches);
    }

    // Advance bye winners to next round
    advanceByeWinners(newBracket);

    setBracket(newBracket);
    setStep('bracket');
  };

  /**
   * Automatically advance bye match winners to next rounds
   */
  const advanceByeWinners = (bracketData: Match[][]): void => {
    for (let roundIdx = 0; roundIdx < bracketData.length - 1; roundIdx++) {
      const currentRound = bracketData[roundIdx];

      currentRound.forEach((match, matchIdx) => {
        if (match.winner && match.isByeMatch) {
          const nextRoundIdx = roundIdx + 1;
          const nextMatchIdx = Math.floor(matchIdx / 2);
          const nextMatch = bracketData[nextRoundIdx][nextMatchIdx];
          const slotPosition = matchIdx % 2 === 0 ? 'participant1' : 'participant2';

          // Place winner in next round
          nextMatch[slotPosition] = match.winner;

          // If both slots filled and one is a bye, auto-advance
          if (nextMatch.participant1 && nextMatch.participant2) {
            if (nextMatch.participant1 === null || nextMatch.participant2 === null) {
              nextMatch.winner = nextMatch.participant1 || nextMatch.participant2;
              nextMatch.isByeMatch = true;
            }
          }
        }
      });
    }
  };

  // ============================================================================
  // WINNER SELECTION
  // ============================================================================

  const selectWinner = (roundIndex: number, matchIndex: number, participant: Participant): void => {
    const newBracket = [...bracket];
    const match = newBracket[roundIndex][matchIndex];

    // Set winner
    match.winner = participant;

    // Advance to next round
    if (roundIndex < newBracket.length - 1) {
      const nextRoundIdx = roundIndex + 1;
      const nextMatchIdx = Math.floor(matchIndex / 2);
      const nextMatch = newBracket[nextRoundIdx][nextMatchIdx];
      const slotPosition = matchIndex % 2 === 0 ? 'participant1' : 'participant2';

      nextMatch[slotPosition] = participant;

      // Check if next match can auto-advance (opponent is bye or already determined)
      if (nextMatch.participant1 && nextMatch.participant2) {
        // Both slots filled, ready to play
      } else if (nextMatch.participant1 || nextMatch.participant2) {
        // One slot filled, check if other will be bye
        const filledSlot = nextMatch.participant1 || nextMatch.participant2;
        const siblingMatchIdx = matchIndex % 2 === 0 ? matchIndex + 1 : matchIndex - 1;
        const siblingMatch = newBracket[roundIndex][siblingMatchIdx];

        if (!siblingMatch || siblingMatch.winner) {
          // Sibling match complete or doesn't exist, can auto-advance if opponent is null
          if (!nextMatch.participant1 || !nextMatch.participant2) {
            nextMatch.winner = filledSlot;
            nextMatch.isByeMatch = true;
            // Recursively advance
            if (nextRoundIdx < newBracket.length - 1) {
              selectWinner(nextRoundIdx, nextMatchIdx, filledSlot!);
            }
          }
        }
      }
    }

    // Check if tournament is complete
    if (roundIndex === newBracket.length - 1) {
      setChampion(participant);
      setStep('complete');
    }

    setBracket(newBracket);
  };

  // ============================================================================
  // RESET FUNCTIONS
  // ============================================================================

  const resetTournament = (): void => {
    setStep('setup');
    setTournamentName('');
    setParticipants([]);
    setBracket([]);
    setChampion(null);
  };

  const backToSetup = (): void => {
    setBracket([]);
    setStep('setup');
  };

  // ============================================================================
  // RENDER: SETUP PHASE
  // ============================================================================

  if (step === 'setup') {
    return (
      <div className="min-h-screen p-8 lg:ml-64 lg:mr-80">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-[#ffd803] border-4 border-black p-6 mb-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <button
              onClick={onBack}
              className="mb-4 bg-[#001858] text-[#ffd803] px-4 py-2 border-4 border-black hover:bg-[#8bd3dd] hover:text-[#001858] transition-colors font-bold"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              Back
            </button>
            <h1 className="text-4xl font-bold text-[#001858] flex items-center gap-3">
              <FontAwesomeIcon icon={faTrophy} />
              Tournament Setup
            </h1>
          </div>

          {/* Tournament Name */}
          <div className="bg-[#8bd3dd] border-4 border-black p-6 mb-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <label className="block text-[#001858] font-bold text-xl mb-3">
              Tournament Name
            </label>
            <input
              type="text"
              value={tournamentName}
              onChange={(e) => setTournamentName(e.target.value)}
              placeholder="Enter tournament name..."
              className="w-full p-4 border-4 border-black text-[#001858] font-bold text-lg focus:outline-none focus:ring-4 focus:ring-[#f582ae]"
              onKeyPress={(e) => e.key === 'Enter' && document.getElementById('participant-input')?.focus()}
            />
          </div>

          {/* Participants */}
          <div className="bg-[#f582ae] border-4 border-black p-6 mb-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-[#001858] flex items-center gap-2">
                <FontAwesomeIcon icon={faUsers} />
                Participants ({participants.length})
              </h2>
              {participants.length > 0 && (
                <button
                  onClick={shuffleParticipants}
                  className="bg-[#001858] text-[#f582ae] px-4 py-2 border-4 border-black hover:bg-[#ffd803] hover:text-[#001858] transition-colors font-bold"
                >
                  <FontAwesomeIcon icon={faRandom} className="mr-2" />
                  Shuffle
                </button>
              )}
            </div>

            {/* Add Participant Input */}
            <div className="flex gap-3 mb-4">
              <input
                id="participant-input"
                type="text"
                value={participantInput}
                onChange={(e) => setParticipantInput(e.target.value)}
                placeholder="Enter participant name..."
                className="flex-1 p-4 border-4 border-black text-[#001858] font-bold text-lg focus:outline-none focus:ring-4 focus:ring-[#ffd803]"
                onKeyPress={(e) => e.key === 'Enter' && addParticipant()}
              />
              <button
                onClick={addParticipant}
                className="bg-[#001858] text-[#ffd803] px-6 py-4 border-4 border-black hover:bg-[#8bd3dd] hover:text-[#001858] transition-colors font-bold"
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>

            {/* Participant List */}
            {participants.length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {participants.map((participant, index) => (
                  <div
                    key={participant.id}
                    className="bg-white border-4 border-black p-4 flex items-center justify-between"
                  >
                    <span className="text-[#001858] font-bold text-lg">
                      {index + 1}. {participant.name}
                    </span>
                    <button
                      onClick={() => removeParticipant(participant.id)}
                      className="bg-[#001858] text-[#f582ae] px-4 py-2 border-4 border-black hover:bg-red-600 hover:text-white transition-colors font-bold"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#001858] text-center py-8 text-lg">
                No participants yet. Add at least 2 to start!
              </p>
            )}
          </div>

          {/* Generate Button */}
          <button
            onClick={generateBracket}
            disabled={participants.length < 2 || !tournamentName.trim()}
            className={`w-full p-6 border-4 border-black font-bold text-2xl shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all ${
              participants.length >= 2 && tournamentName.trim()
                ? 'bg-[#ffd803] text-[#001858] hover:shadow-[12px_12px_0px_0px_rgba(0,24,88,1)] hover:translate-x-[-4px] hover:translate-y-[-4px] cursor-pointer'
                : 'bg-gray-400 text-gray-600 cursor-not-allowed'
            }`}
          >
            <FontAwesomeIcon icon={faTrophy} className="mr-3" />
            Generate Tournament Bracket
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: BRACKET PHASE
  // ============================================================================

  if (step === 'bracket' || step === 'complete') {
    return (
      <div className="min-h-screen p-8 lg:ml-64 lg:mr-80">
        <div className="max-w-full mx-auto">
          {/* Header */}
          <div className="bg-[#ffd803] border-4 border-black p-6 mb-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <button
              onClick={backToSetup}
              className="mb-4 bg-[#001858] text-[#ffd803] px-4 py-2 border-4 border-black hover:bg-[#8bd3dd] hover:text-[#001858] transition-colors font-bold"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              Back to Setup
            </button>
            <h1 className="text-4xl font-bold text-[#001858] flex items-center gap-3">
              <FontAwesomeIcon icon={faTrophy} />
              {tournamentName}
            </h1>
            {champion && (
              <div className="mt-4 bg-[#001858] text-[#ffd803] p-4 border-4 border-black animate-pulse">
                <p className="text-2xl font-bold flex items-center gap-3">
                  <FontAwesomeIcon icon={faTrophy} className="text-3xl" />
                  Champion: {champion.name}!
                </p>
              </div>
            )}
          </div>

          {/* Bracket */}
          <div className="overflow-x-auto pb-8">
            <div className="flex gap-8 min-w-max items-start">
              {bracket.map((round, roundIndex) => {
                // Calculate vertical spacing for bracket pyramid effect
                const spacingMultiplier = Math.pow(2, roundIndex);
                const matchHeight = 240; // Height of a match box including padding
                const baseGap = 40; // Minimum gap between matches in Round 1

                // Gap increases exponentially with more dramatic spacing
                const gapBetweenMatches = baseGap + (matchHeight * (spacingMultiplier - 1)) + (baseGap * (spacingMultiplier - 1));

                // Top margin to center matches relative to previous round
                const topMargin = roundIndex === 0 ? 0 : ((matchHeight + baseGap) / 2) * (spacingMultiplier - 1);

                return (
                  <div key={roundIndex} className="flex flex-col">
                    {/* Round Header */}
                    <div className="bg-[#8bd3dd] border-4 border-black p-4 text-center shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] mb-4">
                      <h3 className="text-xl font-bold text-[#001858]">
                        {getRoundName(roundIndex, bracket.length)}
                      </h3>
                    </div>

                    {/* Matches */}
                    <div
                      className="flex flex-col"
                      style={{
                        gap: `${gapBetweenMatches}px`,
                        marginTop: `${topMargin}px`
                      }}
                    >
                      {round.map((match) => (
                      <div
                        key={match.id}
                        className="bg-white border-4 border-black p-4 w-64 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
                      >
                        {/* Participant 1 */}
                        <button
                          onClick={() =>
                            match.participant1 &&
                            !match.winner &&
                            selectWinner(roundIndex, match.matchIndex, match.participant1)
                          }
                          disabled={!match.participant1 || !!match.winner}
                          className={`w-full p-3 mb-2 border-4 border-black font-bold text-left transition-all ${
                            match.winner?.id === match.participant1?.id
                              ? 'bg-[#ffd803] text-[#001858]'
                              : match.participant1 && !match.winner
                              ? 'bg-[#f582ae] text-[#001858] hover:bg-[#ffd803] cursor-pointer'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {match.participant1?.name || 'TBD'}
                        </button>

                        {/* VS Divider */}
                        <div className="text-center text-[#001858] font-bold text-sm my-2">VS</div>

                        {/* Participant 2 */}
                        <button
                          onClick={() =>
                            match.participant2 &&
                            !match.winner &&
                            selectWinner(roundIndex, match.matchIndex, match.participant2)
                          }
                          disabled={!match.participant2 || !!match.winner}
                          className={`w-full p-3 border-4 border-black font-bold text-left transition-all ${
                            match.winner?.id === match.participant2?.id
                              ? 'bg-[#ffd803] text-[#001858]'
                              : match.participant2 && !match.winner
                              ? 'bg-[#f582ae] text-[#001858] hover:bg-[#ffd803] cursor-pointer'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {match.participant2?.name || 'TBD'}
                        </button>

                        {/* Match Status */}
                        {match.winner && (
                          <div className="mt-3 text-center">
                            <span className="inline-block bg-[#001858] text-[#ffd803] px-3 py-1 text-sm font-bold border-2 border-black">
                              Winner: {match.winner.name}
                            </span>
                          </div>
                        )}
                      </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Reset Button */}
          {champion && (
            <div className="text-center mt-8">
              <button
                onClick={resetTournament}
                className="bg-[#f582ae] text-[#001858] px-8 py-4 border-4 border-black hover:bg-[#ffd803] transition-colors font-bold text-xl shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
              >
                Start New Tournament
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default Tournament;
